import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  MessageCircle,
  Menu,
  ShoppingBag,
  Bell,
  Calendar,
  Bookmark,
  Users,
  Search,
  Archive,
  FolderOpen,
  Video,
  Settings,
  LogOut,
  CircleHelp,
  MessageSquareWarning,
  Home,
  X,
} from "lucide-react";
import Image from "react-bootstrap/Image";
import { useTranslation } from "react-i18next";
import DarkModeSwitch from "./DarkModeSwitch";
import {
  AWS_URL,
  REDUX_LOCAL_STORE_KEY,
  USER_TYPES,
} from "../components/config/constants";
import { notificationActions } from "../redux/actions/notification.action";
import { userActions } from "../redux/actions/user.actions";
import { ipInfoActions } from "../redux/actions/ipInfo.actions";
import { SocketContext } from "../context/SocketContext";
import { StarReviewModule } from "./common/StarReviewModule";
import NavDropdown from "react-bootstrap/NavDropdown";
import { MultiNumber } from "./common/MultiNumber";
import i18n from "../i18n";
import Box from "@mui/material/Box";
import DefaultAvatar from "./common/DefaultAvatar";
import { getUserProfilePic } from "./helper/GeneralHelper";
import AccountVerifyTick from "./common/AccountVerifyTick";
import UserService from "../services/UserService";
import AuthService from "../services/AuthService";
import ChatService from "../services/ChatService";
import NotificationService from "../services/NotificationService";
import GeneralService from "../services/GeneralService";

const navItems = [
  {
    to: "/",
    icons: { inactive: Home, active: Home },
    label: "common.newsfeed",
    onlyCreator: false,
  },
  {
    to: "/clips",
    icons: { inactive: Video, active: Video },
    label: "common.clips",
    onlyCreator: false,
  },
  {
    to: "/queue",
    icons: { inactive: Calendar, active: Calendar },
    label: "common.queue",
    onlyCreator: true,
    userType: "CREATOR",
  },
  {
    to: "/bookmarks",
    icons: { inactive: Bookmark, active: Bookmark },
    label: "common.bookmark",
    onlyCreator: false,
  },
  {
    to: "/my-purchases",
    icons: { inactive: ShoppingBag, active: ShoppingBag },
    label: "common.myPurchases",
    onlyCreator: false,
  },
  {
    to: "/subscription-management",
    icons: { inactive: Users, active: Users },
    label: "common.subscriptions",
    onlyCreator: false,
  },
  {
    to: "/find-a-creator",
    icons: { inactive: Search, active: Search },
    label: "common.findcreator",
    onlyCreator: false,
  },
  {
    to: "/archive",
    icons: { inactive: Archive, active: Archive },
    label: "common.archive",
    userType: "CREATOR",
    onlyCreator: true,
  },
  {
    to: "/collection",
    icons: { inactive: FolderOpen, active: FolderOpen },
    label: "common.collections",
    userType: "CREATOR",
    onlyCreator: true,
  },
  {
    to: "/settings/account-information",
    icons: { inactive: Settings, active: Settings },
    label: "common.settings",
    onlyCreator: false,
  },
  {
    to: "",
    icons: null,
    label: null,
    onlyCreator: false,
  },
];

const notificationDotStyle = {
  position: "absolute",
  top: "8px",
  right: "4px",
  height: "8px",
  width: "8px",
  background: "var(--primary-red-night, #C00)",
  borderRadius: "99px",
};

const searchBoxContainerStyle = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignContent: "center",
  justifyContent: "flex-start",
  alignItems: "center",
};

const mainScrollClipsStyle = {
  height: "100dvh",
  position: "relative",
  top: "-64px",
  width: "100%",
  overflowX: "hidden",
};

const mainScrollDefaultStyle = {
  height: "calc(100dvh - 64px)",
  overflowY: "scroll",
  width: "100%",
  overflowX: "hidden",
};

const Header = ({ children }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { socket } = useContext(SocketContext) || {};

  const loggedUserInfo = useSelector((state) => state.authentication);
  const notificationCountInfo = useSelector((state) => state.notificationCountInfo);
  const notificationApiCallInfo = useSelector((state) => state.notificationApiCallInfo);

  const [theme, setTheme] = useState(localStorage.getItem("theme"));
  const [isOpen, setIsOpen] = useState(false);
  const [, setActiveItem] = useState(null);
  const [isNoti, setIsNoti] = useState(false);
  const [, setLoading] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [newMessageNotification, setNewMessageNotification] = useState(false);
  const [creatorList, setCreatorList] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [userData, setUserData] = useState({
    name: "",
    website: "",
    description: "",
    instagram: "",
    twitter: "",
    snapChat: "",
    telegram: "",
    reddit: "",
    twitch: "",
    youtube: "",
    amazon: "",
    tiktok: "",
    spotify: "",
    profilePicture: "",
    userName: "",
    email: "",
    phoneNumber: "",
    countryCode: "",
    unreadMessageCount: 0,
  });

  const toggleSideBar = useCallback(() => setIsOpen((prev) => !prev), []);
  const handleCloseSideBar = useCallback(() => setIsOpen(false), []);
  const toggleisNoti = useCallback(() => setIsNoti((prev) => !prev), []);

  const getUnreadMessage = useCallback(() => {
    ChatService.getUnreadMessage()
      .then((res) => {
        setUnreadMessageCount(res?.data?.data?.count);
      })
      .catch(() => {});
  }, []);

  const handleMessageNotification = useCallback(() => {
    socket?.emit(
      "update_status_new_message_notification",
      loggedUserInfo?.userData?._id
    );
  }, [socket, loggedUserInfo?.userData?._id]);

  const getEmojisList = useCallback(() => {
    GeneralService.emojisList()
      .then((res) => {
        if (res.status === 200) {
          localStorage.setItem("emojis", JSON.stringify(res?.data?.data));
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const getUserDetails = useCallback(() => {
    setLoading(true);
    if (JSON.parse(localStorage.getItem(REDUX_LOCAL_STORE_KEY)) === null) {
      history.push("/");
    } else {
      if (
        JSON.parse(localStorage.getItem(REDUX_LOCAL_STORE_KEY))
          ?.authentication !== undefined
      ) {
        let params = {
          userName: JSON.parse(localStorage.getItem(REDUX_LOCAL_STORE_KEY))
            .authentication?.userData?.userName,
        };

        UserService.userDetails({ ...params })
          .then((res) => {
            if (res.status === 200) {
              if (
                localStorage.getItem("i18nextLng") !==
                res?.data?.data?.webLanguageCode
              ) {
                localStorage.setItem(
                  "i18nextLng",
                  res?.data?.data?.webLanguageCode
                );
                i18n.changeLanguage(res?.data?.data?.webLanguageCode);
              }

              setLoading(false);
              setUserData(res.data.data);
              let userType = JSON.parse(
                localStorage.getItem(REDUX_LOCAL_STORE_KEY)
              ).authentication.userData.userType;
              let newUserDetails = JSON.parse(
                localStorage.getItem(REDUX_LOCAL_STORE_KEY)
              );
              newUserDetails.authentication.userData.subscribersVisibility =
                res.data.data.subscribersVisibility === false ? false : true;
              newUserDetails.authentication.userData.userType =
                res.data.data.userType;
              localStorage.setItem(
                REDUX_LOCAL_STORE_KEY,
                JSON.stringify(newUserDetails)
              );
              if (
                userType !== res.data.data.userType &&
                userType !== undefined
              ) {
                window.location.reload();
              }
            }
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        history.push("/");
      }
    }
  }, [history]);

  const getUnreadNotificationCount = useCallback(() => {
    NotificationService.unreadNotificationCount()
      .then((res) => {
        if (res.status === 200) {
          dispatch(notificationActions.addNotificationCount(res?.data?.data?.totalCount));
          dispatch(notificationActions.callNotificationApi(false));
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch]);

  const handleClick = useCallback(() => {
    setSearchInput("");
    setCreatorList([]);
  }, []);

  const handleTouch = useCallback(() => {
    setSearchInput("");
    setCreatorList([]);
  }, []);

  // componentDidMount
  useEffect(() => {
    getEmojisList();

    const pathname = location.pathname;
    const matchingItem = navItems.find((item) => pathname.startsWith(item.to));
    if (matchingItem) {
      setActiveItem(matchingItem.to);
    }

    socket?.on("receive_message", () => {
      getUnreadMessage();
    });

    window.addEventListener("click", handleClick);
    window.addEventListener("touch", handleTouch);
    getUnreadMessage();
    window.scrollTo(0, 1);
    getUserDetails();

    socket?.on("send_notification_count", (data) => {
      dispatch(notificationActions.addNotificationCount(data?.totalCount));
    });

    socket?.on("new_message_notification_received", (data) => {
      setNewMessageNotification(data);
    });

    socket?.on("update_status_new_message_notification_received", (data) => {
      setNewMessageNotification(data);
    });

    if (Boolean(notificationApiCallInfo?.callNotificationApi) === true) {
      getUnreadNotificationCount();
    }

    socket?.on("update_message_readed_status", () => {
      getUnreadMessage();
    });

    // componentWillUnmount
    return () => {
      window.removeEventListener("click", handleClick);
      window.removeEventListener("touch", handleTouch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate - handle chat navigation message notification
  useEffect(() => {
    if (
      location.pathname === "/chats" &&
      newMessageNotification === true
    ) {
      handleMessageNotification();
    }
  }, [location.pathname, newMessageNotification, handleMessageNotification]);

  const handleNavLinkClick = useCallback(
    (to) => {
      setActiveItem(to);
      handleCloseSideBar();
    },
    [handleCloseSideBar]
  );

  const handleTextChangeSearch = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  // Search submit when searchInput changes
  useEffect(() => {
    if (searchInput === "") {
      setCreatorList([]);
    } else {
      setLoading(true);
      let params = {
        search: searchInput,
        page: 0,
        limit: 5,
        sortMethod: 1,
      };

      UserService.creatorList({ ...params })
        .then((res) => {
          if (res.status === 200) {
            setCreatorList(res?.data?.data?.data);
            setLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [searchInput]);

  const handleLogout = useCallback(() => {
    let params = {
      sid: loggedUserInfo?.userData?.sessionDetails?.sid,
    };
    localStorage.removeItem(REDUX_LOCAL_STORE_KEY);
    AuthService.logout({ ...params })
      .then((res) => {
        if (res.data.status === 200) {
          dispatch(userActions.logout());
          dispatch(ipInfoActions.removeIpInfo());
          window.location.reload();
        }
      })
      .catch(() => {
        window.location.reload();
        setLoading(false);
      });
  }, [dispatch, loggedUserInfo?.userData?.sessionDetails?.sid]);

  const navClass = `${isOpen ? " nav-active" : ""}`;
  const notiClass = `${isNoti ? " show" : ""}`;
  const closeClass = `${isOpen ? "active" : ""}`;

  const width = window.innerWidth;
  var isMobile = width <= 512;
  var isTab = width <= 1024;

  const titleMore = (
    <div>
      <Menu size={20} style={{ marginRight: "8px" }} />
      <span>{t("common.more")}</span>
    </div>
  );

  return (
    <>
      <div
        className="nav-header bg-white border-0 rc-header"
        style={{ height: "64px" }}
      >
        <div className="nav-top">
          <Link
            to="/"
            style={{ paddingLeft: isMobile ? "0px" : "24px", gap: "7.24px" }}
          >
            <img
              className="logo-red-circle"
              src={`${AWS_URL}logo/logoRedCircle.png`}
              alt=""
            ></img>
            <img
              className="logo-red-circle"
              src={`${AWS_URL}logo/${
                theme === "theme-dark"
                  ? "RedCircleRed.png"
                  : "RedCircleBlack.png"
              }`}
              alt=""
            ></img>
          </Link>

          {!isMobile && !isTab && (
            <div className="position-relative d-flex align-items-center" style={{ marginLeft: "24px" }}>
              <div className="form-group mb-0 icon-input">
                <i className="feather-search text-grey-400 searchIcon"></i>
                <input
                  type="text"
                  placeholder={t("common.search")}
                  value={searchInput}
                  onChange={handleTextChangeSearch}
                  className="bg-gray search-input fw-500 w300 pt-2 pb-2 pe-3"
                  style={{
                    border: "1px solid #EEE",
                    borderRadius: "40px",
                    height: "38px",
                    fontSize: "14px",
                  }}
                />
              </div>
              {searchInput?.length > 2 ? (
                <div className="input-search-icon pointer">
                  <i className="feather-arrow-right-circle font-lg text-grey-400 ms-auto"></i>
                </div>
              ) : null}
              <div className="container-second-search">
                {creatorList.map((value, index) => (
                  <Box
                    key={value._id || index}
                    onClick={() => {
                      history.push(`/${value?.userName}`);
                      window.location.reload();
                      setCreatorList([]);
                    }}
                    sx={searchBoxContainerStyle}
                  >
                    <DefaultAvatar
                      id={value._id}
                      userName={value.name}
                      className="image-2search"
                      height="32px"
                      width="32px"
                      fontSize="14px"
                    >
                      {value.profilePicture ? (
                        <Image
                          alt={value.name}
                          title={value.name}
                          className="image-2search"
                          width={32}
                          height={32}
                          src={`${getUserProfilePic(
                            value._id,
                            value.profilePicture
                          )}`}
                        ></Image>
                      ) : null}
                    </DefaultAvatar>
                    <span className="icon-find-creator">{value.name}</span>
                  </Box>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setActiveItem(null);
              }}
            >
              <Link
                to="/chats"
                onClick={() => {
                  if (window.location.pathname.includes("/chat")) {
                    window.location.reload();
                  }
                  handleMessageNotification();
                }}
                className={`pointer py-2 px-1 text-center ms-2 menu-icon chat-active-btn me-2 mob-menu rc-nav-icon-btn`}
              >
                <div className="navbar-circle d-flex justify-content-center align-items-center">
                  <MessageCircle
                    color="#A3A3A3"
                    size={15}
                    className={` font-sm ${
                      isMobile ? " btn-small" : "btn-round-md"
                    }bg-greylight`}
                  />
                  {unreadMessageCount > 0 && (
                    <div style={notificationDotStyle}></div>
                  )}
                </div>
              </Link>
            </div>

            <div
              onClick={() => {
                setActiveItem(null);
              }}
            >
              <Link
                to="/notifications"
                className={`pointer py-2 px-1 text-center menu-icon chat-active-btn me-2 mob-menu rc-nav-icon-btn`}
              >
                <div className="navbar-circle d-flex justify-content-center align-items-center">
                  <Bell
                    color="#A3A3A3"
                    size={15}
                    className={` font-sm ${
                      isMobile ? " btn-small" : "btn-round-md"
                    }bg-greylight`}
                  />
                  {notificationCountInfo?.notificationCount >
                  0 ? (
                    <div style={notificationDotStyle}></div>
                  ) : null}
                </div>
              </Link>
            </div>

            {isTab && (
              <Menu
                className="theme-btn rc-nav-icon-btn"
                style={{ fontSize: "32px" }}
                size={32}
                onClick={toggleSideBar}
              />
            )}
          </div>
        </div>
        <span
          className={`p-2 pointer text-center ms-auto menu-icon ${notiClass}`}
          id="dropdownMenu3"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          onClick={toggleisNoti}
        ></span>
        {!isMobile && !isTab && (
          <DarkModeSwitch
            onChangeTheme={() =>
              setTheme((prev) =>
                prev === "theme-dark" ? "theme-white" : "theme-dark"
              )
            }
          />
        )}

        <Link
          to="/chats"
          onClick={handleMessageNotification}
          className={`pointer py-2 px-1 text-center ms-2 menu-icon chat-active-btn rc-nav-icon-btn`}
        >
          <div className="navbar-circle d-flex justify-content-center align-items-center">
            <MessageCircle size={15} color="#A3A3A3" />
            {unreadMessageCount > 0 && (
              <div style={notificationDotStyle}></div>
            )}
          </div>
        </Link>
        <Link
          to="/notifications"
          className={`pointer py-2 px-1 text-center menu-icon chat-active-btn rc-nav-icon-btn`}
        >
          {notificationCountInfo?.notificationCount > 0 ? (
            <span className="notification-ball position-absolute notification-count text-white font-xsssss fw-600"></span>
          ) : null}
          <div className="navbar-circle d-flex justify-content-center align-items-center">
            <Bell size={15} color="#A3A3A3" />
          </div>
        </Link>

        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: 51,
              left: 0,
              backgroundColor: "#29314585",
              zIndex: "200",
              width: "100%",
              height: "100vh",
            }}
            onClick={handleCloseSideBar}
          />
        )}

      </div>
      <div style={{ display: "flex", height: "calc(100dvh - 64px)" }}>
        <nav className={`navigation bgc-white scroll-bar rc-nav-sidebar ${navClass}`}>
          <div className="container">
            {isOpen && (
              <>
                <div className="rc-mobile-sidebar-header bgc-white">
                  <Link
                    to="/"
                    style={{
                      paddingLeft: "24px",
                      gap: "7.24px",
                      paddingTop: "16px",
                    }}
                  >
                    <img
                      className="logo-red-circle"
                      src={`${AWS_URL}logo/logoRedCircle.png`}
                      alt=""
                    ></img>
                    <img
                      className="logo-red-circle"
                      src={`${AWS_URL}logo/${
                        theme === "theme-dark"
                          ? "RedCircleRed.png"
                          : "RedCircleBlack.png"
                      }`}
                      alt=""
                    ></img>
                  </Link>

                  <button
                    onClick={toggleSideBar}
                    className={`rc-mobile-sidebar-close ${closeClass}`}
                  >
                    <X className="text-color-dark" size={22} />
                  </button>
                </div>
                <div className="rc-mobile-search bgc-white">
                  <div className="form-group mb-0 icon-input">
                    <i className="feather-search searchIcon navbar-search-icon"></i>
                    <input
                      type="text"
                      placeholder={t("common.search")}
                      value={searchInput}
                      onChange={handleTextChangeSearch}
                      className="bg-gray search-input rc-search-input fw-500 w250 pt-2 pb-2 pe-3 responsiveSearch"
                    />
                  </div>
                  {searchInput?.length > 2 ? (
                    <div className="input-search-icon pointer">
                      <i className="feather-arrow-right-circle font-lg text-grey-400 ms-auto"></i>
                    </div>
                  ) : null}
                  <div className="container-second-search-mobile">
                    {creatorList.map((value, index) => (
                      <div
                        key={value._id || index}
                        className="rc-suggestion-card"
                        onClick={() => {
                          history.push(`/${value?.userName}`);
                          window.location.reload();
                          setCreatorList([]);
                        }}
                      >
                        <DefaultAvatar
                          id={value._id}
                          userName={value.name}
                          className="image-2search"
                          height="32px"
                          width="32px"
                          fontSize="14px"
                        >
                          {value.profilePicture ? (
                            <Image
                              className="image-2search"
                              width={32}
                              height={32}
                              src={`${getUserProfilePic(
                                value._id,
                                value.profilePicture
                              )}`}
                            ></Image>
                          ) : null}
                        </DefaultAvatar>
                        <span className="icon-find-creator">
                          {value.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rc-darkmode-row bgc-white">
                  <span className="darkThemetext">
                    {t("common.switch")}
                  </span>
                  <DarkModeSwitch
                    onChangeTheme={() =>
                      setTheme((prev) =>
                        prev === "theme-dark"
                          ? "theme-white"
                          : "theme-dark"
                      )
                    }
                  />
                </div>
              </>
            )}
            <div className="nav-content">
              <div className="nav-wrap bg-white bg-transparent-card">
                <div className="rc-profile-card">
                  <Link
                    to="/profile"
                    onClick={() => {
                      setActiveItem(null);
                      handleCloseSideBar();
                    }}
                  >
                    <div className="rc-profile-card__avatar-wrap">
                      <DefaultAvatar
                        id={loggedUserInfo?.userData?._id}
                        userName={loggedUserInfo?.userData?.name}
                        width="52px"
                        height="52px"
                        marginBottom="0"
                        online={true}
                      >
                        {loggedUserInfo?.userData?.profilePicture ? (
                          <Image
                            className="rc-profile-card__avatar"
                            src={getUserProfilePic(
                              loggedUserInfo.userData._id,
                              loggedUserInfo.userData.profilePicture
                            )}
                          ></Image>
                        ) : null}
                      </DefaultAvatar>
                      <div className="rc-profile-card__online"></div>
                    </div>
                  </Link>
                  <Link
                    className="profile-name-container"
                    to="/profile"
                    onClick={handleCloseSideBar}
                    style={{ textDecoration: "none" }}
                  >
                    <span className="rc-profile-card__name">
                      {loggedUserInfo?.userData?.name}{" "}
                      {loggedUserInfo?.userData
                        ?.isCreatorVerified ? (
                        <AccountVerifyTick width={"16px"}></AccountVerifyTick>
                      ) : null}
                    </span>
                    <span className="rc-profile-card__username">
                      @{loggedUserInfo?.userData?.userName}
                    </span>
                  </Link>
                  {loggedUserInfo?.userData?.userType ===
                  USER_TYPES.CREATOR ? (
                    <>
                      {loggedUserInfo?.userData
                        ?.averageRating && (
                        <div style={{ marginTop: "4px" }}>
                          <StarReviewModule
                            averageRating={
                              loggedUserInfo?.userData
                                ?.averageRating
                            }
                          />
                        </div>
                      )}
                      <div className="rc-profile-stats">
                        <div className="rc-profile-stat" title={t("common.postss")}>
                          <span className="rc-profile-stat__value">
                            <MultiNumber
                              numberToFix={
                                userData?.countObject
                                  ?.postTotalCount
                              }
                            />
                          </span>
                          <span className="rc-profile-stat__label">
                            {t("common.postss")}
                          </span>
                        </div>
                        <Link
                          to={"/settings/manage-subscribers"}
                          className="rc-profile-stat"
                          title={t("common.subs")}
                        >
                          <span className="rc-profile-stat__value">
                            <MultiNumber
                              numberToFix={
                                userData?.countObject
                                  ?.subscriberCount
                              }
                            />
                          </span>
                          <span className="rc-profile-stat__label">
                            {t("common.subs")}
                          </span>
                        </Link>
                        <div className="rc-profile-stat" title={t("common.likes")}>
                          <span className="rc-profile-stat__value">
                            <MultiNumber
                              numberToFix={
                                userData?.countObject
                                  ?.reactionCount
                              }
                            />
                          </span>
                          <span className="rc-profile-stat__label">
                            {t("common.likes")}
                          </span>
                        </div>
                        <div className="rc-profile-stat" title={t("common.comments")}>
                          <span className="rc-profile-stat__value">
                            <MultiNumber
                              numberToFix={
                                userData?.countObject
                                  ?.commentCount
                              }
                            />
                          </span>
                          <span className="rc-profile-stat__label">
                            {t("common.comments")}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="navbar-container">
                <ul className="rc-nav-list">
                  {navItems.map((item) =>
                    item.label ? (
                      item.label &&
                      (!item.onlyCreator ||
                        (item.onlyCreator &&
                          loggedUserInfo?.userData?.userType ===
                            USER_TYPES.CREATOR)) ? (
                        <li key={item.to} className="rc-nav-list__item">
                          <NavLink
                            to={item.to}
                            activeClassName=""
                            onClick={() => handleNavLinkClick(item.to)}
                            className={`rc-nav-link${
                              location.pathname === item.to
                                ? " rc-nav-link--active"
                                : ""
                            }`}
                          >
                            {React.createElement(
                              location.pathname === item.to
                                ? item.icons.active
                                : item.icons.inactive,
                              { size: 20 }
                            )}
                            <span>{t(item.label)}</span>
                          </NavLink>
                        </li>
                      ) : null
                    ) : (
                      <li key="more" className="rc-nav-list__item rc-nav-more">
                        <NavDropdown
                          title={titleMore}
                          className="menudropdown"
                        >
                          <NavDropdown.Item
                            href="/support"
                            className="rc-nav-link"
                          >
                            <MessageSquareWarning size={20} />
                            <span>{t("common.support")}</span>
                          </NavDropdown.Item>
                          <NavDropdown.Item
                            href="/Faq"
                            className="rc-nav-link"
                          >
                            <CircleHelp size={20} />
                            <span>{t("common.faq")}</span>
                          </NavDropdown.Item>
                          <NavDropdown.Item
                            className="rc-nav-link"
                            onClick={handleLogout}
                          >
                            <LogOut size={20} />
                            <span>{t("common.logout")}</span>
                          </NavDropdown.Item>
                        </NavDropdown>
                      </li>
                    )
                  )}
                </ul>
              </div>
              {isMobile || isTab ? (
                <div className="rc-nav-footer">
                  <Link to="/terms-conditions">
                    {t("common.terms")}
                  </Link>
                  <span className="rc-footer-dot" />
                  <Link to="/privacy-policy">
                    {t("common.privacy")}
                  </Link>
                  <span className="rc-footer-dot" />
                  <Link to="/complaint">
                    {t("terms.complaint")}
                  </Link>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        </nav>
        <div
          id="mainScroll"
          style={
            (location.pathname === "/clips" ||
              location.pathname.includes("single-clip")) &&
            !isMobile
              ? mainScrollClipsStyle
              : mainScrollDefaultStyle
          }
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default Header;
